/* eslint-disable react/jsx-props-no-spreading */
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import H6 from '@base/H6/H6';
import Close from '@icon/Close';
import { BorderColors, Spacings, BgColors, ShadowColors } from '@styles/index';

// If 'mulitple' prop is 'false'
// Bahave like `TextField` (height = 53) (padding = 16px: 24px)

// If 'mulitple' prop is 'true'
// (height = 61) (padding = 12px)
// Wrapper padding = 12 [4(tag margin) + 7(wrapper padding) + 1(wrapper border)]

const StyledAutoComplete = styled(Autocomplete)`
  &.MuiAutocomplete-root {
    .MuiFormControl-root {
      background-color: ${BgColors.WHITE};
      box-shadow: ${({ theme }) => boxShadowGenerator({ theme }) || 'none'};
      transition: all 400ms ease-in-out;
      :hover {
        box-shadow: ${({ theme }) =>
          boxShadowGenerator({
            yOffset: Spacings.SPACING_2B,
            blur: Spacings.SPACING_2B,
            theme
          }) || 'none'};
      }
      .MuiInputBase-root {
        padding: ${({ multiple }) =>
          multiple ? '7px' : `${Spacings.SPACING_4B} ${Spacings.SPACING_6B}`};
        .MuiInputBase-input {
          ${({ multiple }) => {
            if (multiple) {
              return `
                margin: ${Spacings.SPACING_1B};
                padding: 9px 4px;
              `;
            }

            return `
              padding: 0px;
            `;
          }}
        }
        .MuiInputBase-input::placeholder {
          font-size: 14px;
          color: #808080;
          font-weight: 600;
        }
        &.MuiOutlinedInput-root {
          border-style: solid;
          border-width: 1px;
          border-color: ${({ error }) =>
            error ? BorderColors.RED : BorderColors.GREY};
          transition: border-color 400ms ease-in-out;
        }
        &.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
          display: none;
          pointer-events: none;
        }
      }
    }
  }
  &:hover.MuiAutocomplete-root .MuiFormControl-root .MuiOutlinedInput-root {
    border-color: ${BorderColors.BLUE};
  }
`;

const TagName = styled(H6)`
  margin-right: 10px;
`;

const Tag = styled.div`
  display: flex;
  border: 1px solid ${BorderColors.BLUE};
  border-radius: ${Spacings.SPACING_2B};
  background-color: ${BgColors.WHITE};
  padding: 7px 9px;
  box-shadow: 0px 4px 16px ${ShadowColors.PRIMARY_SHADOW};
  margin: ${Spacings.SPACING_1B};
  align-items: center;
`;

const CloseIcon = styled(Close)`
  cursor: pointer;
`;

const AutoCompleteInput = (props) => {
  const {
    options,
    value,
    getOptionLabel,
    onOptionClick,
    onChangeValue,
    multiple,
    removeTag,
    disabled,
    className,
    placeholder,
    optionKey,
    renderOption,
    disableClearable,
    clearOnBlur,
    disableCloseOnSelect,
    useFilter,
    noOptionsText,
    ListboxProps,
    error
  } = props;
  let finalOptions = options;
  let finalPlaceHolder = placeholder;
  if (multiple) {
    finalPlaceHolder = value?.length > 0 ? '' : placeholder;
    finalOptions = options.filter(
      (lang1) => !value.some((lang2) => lang1[optionKey] === lang2[optionKey])
    );
  }

  const autoCompleteProps = {
    onChange: onOptionClick,
    options: finalOptions,
    disableCloseOnSelect,
    disabled,
    blurOnSelect: !multiple,
    disableOpenOnFocus: true,
    disableClearable,
    clearOnBlur,
    getOptionLabel,
    noOptionsText,
    popupIcon: null,
    multiple,
    className,
    value,
    onInputChange: onChangeValue,
    renderOption,
    ListboxProps,
    error,
    renderTags: (optionValue) => {
      return optionValue.map((option, index) => {
        const label =
          option.name || (getOptionLabel && getOptionLabel(option)) || '';
        return (
          <Tag key={label} index={index}>
            <TagName>{label}</TagName>
            {!disabled && (
              <CloseIcon
                size={Spacings.SPACING_3B}
                onClick={removeTag(option)}
              />
            )}
          </Tag>
        );
      });
    },
    // eslint-disable-next-line react/display-name
    renderInput: (params) => {
      return (
        <TextField
          {...params}
          placeholder={finalPlaceHolder}
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps
          }}
        />
      );
    }
  };

  if (!useFilter)
    autoCompleteProps.filterOptions = (unfilteredOptions) => unfilteredOptions;

  return <StyledAutoComplete {...autoCompleteProps} />;
};

AutoCompleteInput.propTypes = {
  options: PropTypes.arrayOf(PropTypes.any).isRequired,
  getOptionLabel: PropTypes.func,
  onOptionClick: PropTypes.func.isRequired,
  onChangeValue: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  removeTag: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.any),
  placeholder: PropTypes.string,
  optionKey: PropTypes.string,
  renderOption: PropTypes.func,
  disableClearable: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  useFilter: PropTypes.bool,
  noOptionsText: PropTypes.string,
  ListboxProps: PropTypes.objectOf(PropTypes.any),
  error: PropTypes.bool,
  clearOnBlur: PropTypes.bool
};

AutoCompleteInput.defaultProps = {
  getOptionLabel: null,
  disabled: false,
  multiple: false,
  className: '',
  value: [],
  placeholder: null,
  removeTag: () => {},
  optionKey: 'id',
  renderOption: undefined,
  disableClearable: true,
  disableCloseOnSelect: false,
  useFilter: false,
  noOptionsText: '',
  ListboxProps: {},
  error: false,
  clearOnBlur: true
};

export default AutoCompleteInput;
